import DOMPurify from 'dompurify'
import { Clipboard } from '@capacitor/clipboard'

import { server } from '../config'
import { PlayAudioFromUrl } from 'capacitor-play-audio-from-url'

let audioQueue: string[] = []
let audioPlaying = false

export const enqueueAudio = async (audioPath: string) => {
  const url = new URL(audioPath)
  audioPath = `${server}${url.pathname}`

  // Add the MediaObject to the queue
  audioQueue.push(audioPath)

  if (!audioPlaying) {
    // If no audio is currently playing, start playing the next audio
    await playNextAudio()
  }
}


const playAudio = async (audioPath: string) => {
  const audioFileName = audioPath.split('/').pop()
  if (!audioFileName) {
    return
  }
  try {
    await PlayAudioFromUrl.play({ url: audioPath })
  } catch (error) {
    return
  }
}
const playNextAudio = async () => {
  // If the queue is empty, do nothing
  if (audioQueue.length === 0) {
    audioPlaying = false
    return
  }

  // Get the next MediaObject from the queue
  const nextAudio = audioQueue.shift()
  if (!nextAudio) return

  // Play the audio
  audioPlaying = true
  await playAudio(nextAudio)
  await playNextAudio()
  audioPlaying = false
}
export const copyToClipboard = (text: string) => {
  return Clipboard.write({ string: text })
}

export const text_from_html = (html: string) => {
  // gets rid of html tags, converts &nbsp; and other html entities.
  const div = document.createElement('div')
  div.innerHTML = html
  return div.textContent || div.innerText || ''
}

export const pronounce_builtin = (text: string, language: string, slow?: boolean) => {
  text = text_from_html(text)

  // Support for adding pronunciation e.g. faux /fó/ (we don't want to pronounce that part)
  const textToPronounce = text.split(' /')[0]


  const utterance = new SpeechSynthesisUtterance(textToPronounce)
  utterance.lang = language
  utterance.rate = slow ? 0.45 : 0.9
  console.log('Pronouncing', textToPronounce, language, slow)
  window.speechSynthesis.speak(utterance)
}

const purifyConfig = { ALLOWED_TAGS: ['em', 'br', 'img', 'table', 'tr', 'td', 'wbr'] }

export const sanitizeCell = (cell: string) => {
  let sanitized = DOMPurify.sanitize(cell, purifyConfig)
  sanitized = sanitized.replace(/<br>$/, '').replace(/^<br>/, '')
  return sanitized
}

export const isDesktop = () => {
  return !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

/**
 * Places the caret at a specified position within a contenteditable element
 * @param element - The contenteditable element to place the caret in
 * @param position - The position to place the caret at: 'start', 'end', or a specific index
 */
export type Position = 'start' | 'end' | number
export const placeCaret = (element: HTMLElement, position: Position): void => {
  // Focus the element first
  element.focus()
  console.log('placeCaret', position)

  // Create range and get selection
  const range = document.createRange()
  const sel = window.getSelection()

  // Ensure there's content to place caret in
  if (!element.firstChild) {
    element.appendChild(document.createTextNode(''))
  }

  if (position === 'start') {
    // Place at the beginning
    range.setStart(element.firstChild!, 0)
  } else if (position === 'end') {
    // Place at the end
    const lastChild = element.lastChild!
    const offset = lastChild.nodeType === Node.TEXT_NODE
      ? lastChild.textContent?.length || 0
      : 0
    range.setStart(lastChild, offset)
  } else {
    // Try to place at specific position
    if (element.firstChild && element.firstChild.nodeType === Node.TEXT_NODE) {
      const textLength = element.firstChild.textContent?.length || 0
      const safePos = Math.min(Math.max(0, position), textLength)
      range.setStart(element.firstChild, safePos)
    } else {
      // Fallback to start if we can't place at specific position
      range.setStart(element.firstChild!, 0)
    }
  }

  // Collapse the range to a single point (the caret)
  range.collapse(true)

  // Apply the selection
  sel?.removeAllRanges()
  sel?.addRange(range)
}

/**
 * Gets the current caret position in a contenteditable element
 * Returns 'start' if at the beginning, 'end' if at the end, or the numeric position
 *
 * @param element - The contenteditable element to check
 * @returns The caret position as 'start', 'end', or a number
 */
export const getCaretPosition = (element: HTMLElement): Position => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return 'start'; // Default to start if no selection
  }

  const range = selection.getRangeAt(0);
  const caretPos = range.startOffset;

  // If caret is at the beginning of the element or its first text node
  if (caretPos === 0) {
    // Check if we're in the first possible position in the element
    const firstTextNode = getFirstTextNode(element);
    if (range.startContainer === firstTextNode ||
        range.startContainer === element ||
        range.startContainer === element.firstChild) {
      return 'start';
    }
  }

  // If caret is at the end of the content
  const lastTextNode = getLastTextNode(element);
  const isLastNode = (range.startContainer === lastTextNode ||
                      range.startContainer === element.lastChild ||
                      range.startContainer === element);

  const nodeLength = range.startContainer.nodeType === Node.TEXT_NODE
    ? range.startContainer.textContent?.length || 0
    : range.startContainer.childNodes.length;

  if (isLastNode && caretPos === nodeLength) {
    return 'end';
  }

  // Otherwise return the numeric position
  return caretPos;
};

/**
 * Gets the first text node in an element, recursively searching through children
 */
function getFirstTextNode(element: Node): Text | null {
  if (element.nodeType === Node.TEXT_NODE) {
    return element as Text;
  }

  for (let i = 0; i < element.childNodes.length; i++) {
    const textNode = getFirstTextNode(element.childNodes[i]);
    if (textNode) {
      return textNode;
    }
  }

  return null;
}

/**
 * Gets the last text node in an element, recursively searching through children
 */
function getLastTextNode(element: Node): Text | null {
  if (element.nodeType === Node.TEXT_NODE) {
    return element as Text;
  }

  for (let i = element.childNodes.length - 1; i >= 0; i--) {
    const textNode = getLastTextNode(element.childNodes[i]);
    if (textNode) {
      return textNode;
    }
  }

  return null;
}
